<template>
  <div class="tickets-container">
    <header>
      <div class="tickets-counter">
        <h4>
          Messenger
        </h4>
        <span>
          {{ unreadMessages }}
        </span>
      </div>

      <b-dropdown
        ref="newMessengerOptionsDropdown"
        aria-role="list"
        position="is-bottom-left"
        multiple
        max-height="80vh"
        scrollable
        class="dropdown-tickets"
        @active-change="filterIsOpen = $event"
      >
        <template #trigger>
          <b-icon :icon="filterIsOpen ? 'clear' : 'customize'" :class="filterIsOpen ? 'back' : ''" class="dropdown-tickets-icon" />
        </template>
        <div class="dropdown-tickets-items ">
          <div>
            <p class="dropdown-tickets-items-title">
              {{ $t('newMessenger.status') }}
            </p>
            <div class="dropdown-tickets-checkbox">
              <b-checkbox v-model="filter.myTickets">
                {{ $t('newMessenger.myTickets') }}
              </b-checkbox>
              <b-checkbox v-model="filter.othersTickets">
                {{ $t('newMessenger.othersTickets') }}
              </b-checkbox>
              <b-checkbox v-model="filter.unassigned">
                {{ $t('newMessenger.unassigned') }}
              </b-checkbox>
              <div class="horizontal-line" />
              <b-checkbox v-model="filter.ticketOpen">
                {{ $t('newMessenger.open') }}
              </b-checkbox>
              <b-checkbox v-model="filter.ticketClosed">
                {{ $t('newMessenger.closed') }}
              </b-checkbox>
              <b-checkbox v-model="filter.ticketArchived">
                {{ $t('newMessenger.archived') }}
              </b-checkbox>
            </div>
          </div>
          <div>
            <p class="dropdown-tickets-items-title">
              {{ $t('newMessenger.language') }}
            </p>
            <div class="dropdown-tickets-flag">
              <img
                v-for="(value, key) in flags"
                :key="key"
                :src="value"
                class="dropdown-tickets-flag-circle"
                :class="isSelectedLanguage(key) ? 'selected' : ''"
                @click="toggleLanguageSelection(key)"
              >
            </div>
          </div>
          <div class="dropdown-tickets-buttons">
            <b-button class="dropdown-tickets-button-reset" @click="filtrTicketsClear()">
              {{ $t('newMessenger.reset') }}
            </b-button>
            <b-button class="dropdown-tickets-button-apply" type="is-primary" @click="applyFilters()">
              {{ $t('newMessenger.applyFilters') }}
            </b-button>
          </div>
        </div>
      </b-dropdown>
    </header>
    <div class="tickets-search-container">
      <b-input
        v-model="filter.filter"
        :lazy="true"
        :placeholder="$t('fields.search')"
        icon="magnify"
        rounded
        @input="search()"
        @keyup.native.enter="updateFilters()"
        @blur="updateFilters()"
      />
    </div>
    <div v-if="$store.state.websocket.loadingTicketList" class="skeleton-tickets">
      <div v-for="index in 6" :key="index" class="skeleton-ticket">
        <div class="skeleton-flex-row" style="margin-bottom: 12px;">
          <div style="width: 24px;">
            <b-skeleton width="24px" height="24px" circle />
          </div>
          <b-skeleton width="50%" />
        </div>
        <div class="skeleton-flex-row">
          <b-skeleton />
        </div>
      </div>
    </div>
    <div v-else ref="ticketsList" class="tickets-list" @scroll="checkScroll">
      <new-messenger-ticket
        v-for="(ticket, index) in tickets"
        :key="index"
        :ticket="ticket"
        @click.native="emitEnterConversation(ticket.id)"
      />
      <div v-if="$store.state.websocket.loadingTicketListMore" class="scroll-information">
        <b-loading active :is-full-page="false" />
      </div>
    </div>
  </div>
</template>

<script>
import NewMessengerTicket from './NewMessengerTicket'
import pl from '~/marketplace-front-core/static/icons/flags/pl.svg'
import de from '~/marketplace-front-core/static/icons/flags/de.svg'
import en from '~/marketplace-front-core/static/icons/flags/en.svg'
import fr from '~/marketplace-front-core/static/icons/flags/fr.svg'
import es from '~/marketplace-front-core/static/icons/flags/es.svg'
import it from '~/marketplace-front-core/static/icons/flags/it.svg'
import nl from '~/marketplace-front-core/static/icons/flags/nl.svg'
import debounce from '~/marketplace-front-core/utils/debounce'

export default {
  name: 'NewMessengerTickets',
  components: { NewMessengerTicket },
  props: {
    tickets: {
      type: Array,
      default: () => []
    },
    unreadMessages: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      filterIsOpen: false,
      filter: null,
      currentSearchedFilters: null,
      defaultFilter: {
        filter: null,
        myTickets: true,
        othersTickets: false,
        ticketOpen: true,
        ticketClosed: false,
        ticketArchived: false,
        lang: [],
        unassigned: true
      },
      loadTickets: false,
      flags: {
        pl,
        de,
        en,
        fr,
        es,
        it,
        nl
      }
    }
  },
  computed: {
    isDropdownOpenValue () {
      return this.$refs.newMessengerOptionsDropdown ? this.$refs.newMessengerOptionsDropdown.isActive : false
    }
  },
  watch: {
    '$route.query': {
      deep: true,
      handler (query) {
        const hasChanged = this.checkChangesFilter()
        if (hasChanged) {
          this.$emit('reloadTicketList', true)
        }
      }
    },
    filterIsOpen (val) {
      if (!val) {
        this.filter = this._clone(this.currentSearchedFilters)
      }
    }
  },
  created () {
    this.currentSearchedFilters = this._clone(this.defaultFilter)
    this.checkChangesFilter()
  },
  methods: {
    checkChangesFilter () {
      const filter = this._clone(this.currentSearchedFilters)
      const filter2 = this._clone(this.currentSearchedFilters)

      const query = this._clone(this.$route.query)
      Object.keys(query).forEach((key) => {
        if (filter[key] !== undefined) {
          filter[key] = this.getValueFromQuery(query[key])
        }
      })

      this.filter = filter
      this.currentSearchedFilters = this._clone(this.filter)
      this.$emit('changeFilter', this.currentSearchedFilters)

      return JSON.stringify(filter) !== JSON.stringify(filter2)
    },
    getValueFromQuery (value) {
      if (value === 'true') {
        return true
      } else if (value === 'false') {
        return false
      } else if (value === '') {
        return null
      } else {
        return value
      }
    },
    checkScroll ({ target: { scrollTop, clientHeight, scrollHeight } }) {
      if (scrollHeight >= (clientHeight + 20) && !this.loadTickets) {
        if (scrollTop + clientHeight >= (scrollHeight - 20)) {
          this.loadTickets = true
          this.$emit('loadMoreTickets')
        }
      } else if ((scrollTop + clientHeight >= scrollHeight) && !this.loadTickets) {
        this.loadTickets = true
        this.$emit('loadMoreTickets')
      }
    },
    setLoadTickets (status) {
      this.loadTickets = status
    },
    search: debounce(function () {
      this.updateFilters()
    }, 1000),
    applyFilters () {
      this.updateFilters()
      this.toggleDropdown()
    },
    filtrTicketsClear () {
      this.clearFilter()

      this.toggleDropdown()
    },
    updateFilters () {
      const filter = this._clone(this.filter)

      if (filter.filter === '') { filter.filter = null }

      if (JSON.stringify(filter) === JSON.stringify(this.defaultFilter)) {
        this.clearFilter()
      } else if (JSON.stringify(filter) !== JSON.stringify(this.currentSearchedFilters)) {
        let query = this._clone(this.$route.query)
        query = { ...query, ...filter }

        // Wyjście z aktualnie będącego ticketu
        delete query.ticketId

        this.$emit('changeFilter', filter)
        this.currentSearchedFilters = filter

        if (JSON.stringify(this.$route.query) !== JSON.stringify(query)) {
          this.$router.push({ query }, () => {
            this.$emit('reloadTicketList', true)
          })
        } else {
          this.$emit('reloadTicketList', true)
        }

        const ticketsList = this.$refs.ticketsList
        if (ticketsList) {
          ticketsList.scrollTop = 0
        }
      }
    },
    clearFilter () {
      if (JSON.stringify(this.defaultFilter) !== JSON.stringify(this.currentSearchedFilters)) {
        const query = this._clone(this.$route.query)

        delete query.filter
        delete query.myTickets
        delete query.othersTickets
        delete query.ticketOpen
        delete query.ticketClosed
        delete query.ticketArchived
        delete query.lang
        delete query.unassigned

        // Wyjście z aktualnie będącego ticketu
        delete query.ticketId

        if (JSON.stringify(this.$route.query) !== JSON.stringify(query)) {
          this.$router.push({ query })
        }

        this.filter = this._clone(this.defaultFilter)
        this.$emit('changeFilter', this.filter)
        this.currentSearchedFilters = this.filter

        this.$emit('reloadTicketList', true)
      }
    },
    emitEnterConversation (id) {
      this.$emit('enterConversation', id)
    },
    toggleDropdown () {
      this.$refs.newMessengerOptionsDropdown.isActive = !this.$refs.newMessengerOptionsDropdown.isActive
    },
    isSelectedLanguage (flag) {
      return this.filter.lang.includes(flag)
    },
    toggleLanguageSelection (flag) {
      const index = this.filter.lang.indexOf(flag)
      if (index !== -1) {
        this.filter.lang.splice(index, 1)
      } else {
        this.filter.lang.push(flag)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$messenger-border-color: #E7E8E7;
$messanger-button-text-color: #909090;
$messanger-shadow-icon-button: #F8F8F8;
$messenger-selected-color: #E32F30;

.dropdown-tickets {
  :deep(.dropdown-tickets-icon) {
    width: 34px;
    height: 34px;
    border-radius: 50px;
    padding: 6px;
  }

  :deep(.dropdown-tickets-icon)>i {
    font-size: 16px !important;
    color: $messanger-button-text-color;
  }

  :deep(.dropdown-tickets-icon.back)>i {
    font-size: 16px !important;
  }
}

.dropdown-tickets-icon:hover {
  background-color: $messanger-shadow-icon-button;
  transition: background-color 0.2s ease-in-out;
  cursor: pointer;
}

.back {
  background-color: $messanger-shadow-icon-button;
  transition: background-color 0.2s ease-in-out;
  cursor: pointer;
}

.dropdwon-tickets-container-switch {
  display: flex;
  flex-direction: column;
  gap: 10px;

  :deep(.dropdown-tickets-switch) {
    font-size: 14px;
  }
}

.horizontal-line {
  width: 100%;
  height: 1px;
  background-color: #cccccc;
}

:deep(.dropdown-tickets-checkbox) {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
  .b-checkbox {
    display: flex;
  }
}

.dropdown-tickets-items {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  width: 220px;

  .dropdown-tickets-buttons {
    display: flex;
    flex-direction: column;
    gap: 2px;

    button {
      font-size: 14px;
    }

    .dropdown-tickets-button-reset {
      border: none;
      color: $messanger-button-text-color;
    }
    .dropdown-tickets-button-apply {
      font-weight: 600;
    }
  }

  .dropdown-tickets-items-title {
    font-weight: 600;
    margin-bottom: 10px;
  }

  @media (max-width: 1024px) {
    width: 100%;
  }
}

.dropdown-tickets-button {
  background-color: transparent;
  border: 0px;
}

.dropdown-tickets-flag {
  display: flex;
  flex-direction: row;
  justify-content: space-around;

  .dropdown-tickets-flag-circle {
    width: 28px;
    padding: 1px;
    cursor: pointer;
  }

  .selected {
    border: 2px solid $messenger-selected-color;
    border-radius: 50px;
  }
}

.tickets-container {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    height: 64px;
    padding: 0 16px;
    border-bottom: 1px solid $messenger-border-color;

    .tickets-counter {
      display: flex;
      align-items: center;
      gap: 6px;

      h4 {
        display: inline-block;
        font-weight: 600;

        @include touch {
          font-size: 20px;
        }
      }

      span {
        display: inline-flex;
        width: 22px;
        height: 22px;
        align-items: center;
        justify-content: center;
        background-color: $primary;
        border-radius: 50%;
        color: white;
        font-size: 13px;
        font-weight: 600;
      }
    }
  }

  .tickets-search-container {
    flex-shrink: 0;
    min-height: 0;
    padding: 16px;
    border-bottom: 1px solid $messenger-border-color;
    overflow: hidden;

    &:deep(input) {
      height: 40px;
      padding-left: 38px;
      // font-size: 14px;
    }

    &:deep(.icon) {
      width: 30px !important;
      left: 8px;

      i {
        font-size: 14px !important;
      }
    }
  }

  .tickets-button-container {
    flex-shrink: 0;
    min-height: 0;
    padding: 16px;
    border-bottom: 1px solid $messenger-border-color;
    overflow: hidden;
  }

  .tickets-list {
    position: relative;
    overflow: hidden auto;
    -webkit-overflow-scrolling: touch;

    & > :deep(.ticket) {
      border-bottom: 1px solid $messenger-border-color;
    }

    .scroll-information {
      position: sticky;
      left: calc(50% - 24px);
      right: 0;
      bottom: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      border-radius: 50%;
      overflow: hidden;
      background-color: white;
      z-index: 100;
      box-shadow: 0 2px 2px rgba($text_color, 0.2);
      :deep(.loading-icon):after {
        width: 2rem;
        height: 2em;
        top: calc(50% - 1em);
        left: calc(50% - 1em);
      }
    }
  }

  .skeleton-tickets {
    .skeleton-ticket {
      padding: 12px 16px;
      border-bottom: 1px solid $messenger-border-color;
      .skeleton-flex-row {
        display: flex;
        align-items: center;
        gap: 10px;
        .b-skeleton {
          margin: 0;
        }
      }
    }
  }
}
</style>
